import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from 'react-router-dom';

// Context and Hooks
import { SnowballProvider, useSnowballContext } from '../contexts/snowball-context';
import { useSnowballAccounts } from '../hooks/useSnowballAccounts';
import { useResponseMessages } from "../hooks/useResponseMessage";

// Services
import { svc_getSnowballTransactionIds, svc_deleteSnowballTransactions, svc_createSnowballData, svc_createTransaction, svc_createException } from "../services/transaction.service";

// Utilities
import { normalizeExceptionFormData } from "../utilities/data-normalization";

// Components
import StarterLump from '../components/Snowball/StarterLump';
import ActionButtons from '../components/Snowball/ActionButtons';
import SnowballAccount from '../components/Snowball/SnowballAccount';
import SnowballProgress from '../components/Snowball/SnowballProgress';
import SnowballAccountList from '../components/Snowball/SnowballAccountList';
import ExistingTransactionsOverlay from "../components/Snowball/ExistingTransactionsOverlay";

const SnowballContent = () => {
    const { getAccessTokenSilently, user } = useAuth0();
    const { addMessage } = useResponseMessages();
    const [isCreating, setIsCreating] = useState(false);
    const [planCompleted, setPlanCompleted] = useState(false);
    
    // Context state management
    const {
        snowballStarterLump,
        debitAccountID,
        accounts,
        transactions,
        aggregateResults,
        hasAccounts,
        hasExistingTransactions,
        allAccountsHaveSchedules,
        // Actions
        setSnowballStarterLump,
        setDebitAccountID,
        setAccounts,
        setExistingTransactionIds,
        calculateSchedules,
        updateAccount
    } = useSnowballContext();

    // Account management hook
    const {
        debtAccounts,
        nonDebtAccounts,
        isLoading: isLoadingAccounts,
        getAccounts,
        handleRefetchAccounts
    } = useSnowballAccounts();

    // Sync debt accounts with context when they change
    useEffect(() => {
        if (debtAccounts && debtAccounts.length > 0) {
            setAccounts(debtAccounts);
        }
    }, [debtAccounts, setAccounts]);

    // Fetch existing snowball transactions on mount
    useEffect(() => {
        const fetchExistingTransactionIds = async () => {
            try {
                const accessToken = await getAccessTokenSilently();
                const { data } = await svc_getSnowballTransactionIds(accessToken, user.sub);
                const existingIds = data && data.length > 0 ? data : null;
                setExistingTransactionIds(existingIds);
                
                // If we have existing transactions, set plan as completed
                if (existingIds && existingIds.length > 0) {
                    setPlanCompleted(true);
                }
            } catch (error) {
                console.error('Error fetching existing transaction IDs:', error);
            }
        };

        fetchExistingTransactionIds();
    }, [getAccessTokenSilently, user.sub, setExistingTransactionIds]);

    // Handle account updates with automatic recalculation
    const handleAccountUpdate = useCallback((updatedAccount) => {
        updateAccount(updatedAccount);
    }, [updateAccount]);

    // Handle account inclusion/exclusion changes
    const handleAccountToggle = useCallback((updatedAccounts) => {
        // Always recalculate with sorting when accounts are toggled
        calculateSchedules(updatedAccounts, true);
    }, [calculateSchedules]);

    // Create transactions directly without overlay
    const handleCreateTransactions = useCallback(async () => {
        if (!transactions || transactions.length === 0) {
            addMessage({
                error: 'No transactions to create.'
            });
            return;
        }

        setIsCreating(true);
        try {
            const accessToken = await getAccessTokenSilently();
            let success = true;
            
            // Create all transactions
            for (const transactionItem of transactions) {
                // Clean up transaction data (add isSnowball flag like original)
                const cleanedTransaction = {
                    ...transactionItem.transaction,
                    startDate: transactionItem.transaction.startDate,
                    endDate: transactionItem.transaction.endDate || null,
                    isSnowball: 1
                };

                // Create the main transaction
                const { data: transactionData, error: transactionError } = await svc_createTransaction(
                    accessToken, 
                    user.sub, 
                    cleanedTransaction
                );

                if (transactionError || !transactionData?.insertId) {
                    console.error('Transaction creation error:', transactionError);
                    success = false;
                    break;
                }

                // Create exceptions if any
                if (transactionItem.exceptions && transactionItem.exceptions.length > 0) {
                    for (const exception of transactionItem.exceptions) {
                        // Use the same normalization as the original overlay
                        const normalizedExceptionData = normalizeExceptionFormData({
                            id: null,
                            transactionId: transactionData.insertId,
                            instanceDate: exception.instanceDate,
                            startDate: exception.startDate,
                            endDate: exception.endDate,
                            amount: exception.amount,
                            isRescheduled: exception.isRescheduled,
                            isCancelled: exception.isCancelled,
                            isPaid: exception.isPaid,
                            highlight: exception.highlight,
                            daysAdjusted: exception.daysAdjusted,
                        });

                        const { error: exceptionError } = await svc_createException(
                            accessToken,
                            user.sub,
                            normalizedExceptionData
                        );

                        if (exceptionError) {
                            console.error('Exception creation error:', exceptionError);
                            success = false;
                            break;
                        }
                    }
                }

                if (!success) break;
            }

            if (success) {
                addMessage({
                    data: 'All transactions created successfully!'
                });
                setPlanCompleted(true);

                // Save aggregate results
                if (aggregateResults) {
                    try {
                        const { error } = await svc_createSnowballData(accessToken, user.sub, aggregateResults);
                        if (error) {
                            addMessage({
                                error: 'Transactions created, but there was an error storing aggregate snowball data.'
                            });
                        }
                    } catch (error) {
                        console.error('Error saving aggregate results:', error);
                    }
                }
            } else {
                addMessage({
                    error: 'There was an error creating some transactions. Please try again.'
                });
            }
        } catch (error) {
            console.error('Error creating transactions:', error);
            addMessage({
                error: 'There was an unexpected error creating transactions.'
            });
        } finally {
            setIsCreating(false);
        }
    }, [transactions, addMessage, getAccessTokenSilently, user.sub, aggregateResults]);

    // Handle deleting existing transactions
    const deleteExistingTransactions = useCallback(async () => {
        try {
            const accessToken = await getAccessTokenSilently();
            const { error } = await svc_deleteSnowballTransactions(accessToken, user.sub);
            
            if (error) {
                addMessage({
                    error: 'There was an error deleting existing snowball transactions.'
                });
            } else {
                addMessage({
                    data: 'Existing snowball transactions deleted successfully.'
                });
                setExistingTransactionIds(null);
                setPlanCompleted(false); // Reset to planning mode
            }
        } catch (error) {
            console.error('Error deleting transactions:', error);
            addMessage({
                error: 'There was an error deleting existing snowball transactions.'
            });
        }
    }, [getAccessTokenSilently, user.sub, addMessage, setExistingTransactionIds]);

    // Memoized non-debt account selector
    const debitAccountSelector = useMemo(() => {
        if (!nonDebtAccounts || nonDebtAccounts.length === 0) {
            return null;
        }

        return (
            <div className="standardFormWrap">
                <h4 style={{ margin: "0 0 1rem 0", color: "#333" }}>
                    Select Payment Account
                </h4>
                <p style={{ margin: "0 0 1rem 0", fontSize: "0.9rem", color: "#666" }}>
                    Choose the account you'll use to make debt payments
                </p>
                <div className="formField">
                    <select
                        value={debitAccountID || ""}
                        onChange={(e) => setDebitAccountID(e.target.value)}
                        className="formField"
                        style={{ width: "100%" }}
                        required={true}
                    >
                        <option value="" disabled>Select an account</option>
                        {nonDebtAccounts.map((account) => (
                            <option key={account.id} value={account.id}>
                                {account.institution} - {account.name} - {account.type}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    }, [nonDebtAccounts, debitAccountID, setDebitAccountID]);

    return (
        <div style={{ 
            width: "1400px", 
            maxWidth: "100%", 
            margin: "0 auto",
            padding: "2rem 1rem"
        }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                <h1 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>
                    Debt Snowball Calculator
                </h1>
                <p style={{ margin: '0', color: '#666', fontSize: '1.1rem' }}>
                    Create a strategic plan to pay off your debts faster and save on interest
                </p>
            </div>

            {/* Starter Lump Sum Input */}
            {!planCompleted && (
                <StarterLump
                    snowballStarterLump={snowballStarterLump}
                    setSnowballStarterLump={setSnowballStarterLump}
                />
            )}

            {/* Payment Account Selector */}
            {!planCompleted && debitAccountSelector}

            {/* Completed Plan View */}
            {planCompleted && (
                <div style={{
                    backgroundColor: '#d1edff',
                    border: '2px solid #007bff',
                    borderRadius: '12px',
                    padding: '2.5rem',
                    margin: '2rem auto',
                    maxWidth: '800px',
                    textAlign: 'center'
                }}>
                    <h2 style={{ margin: '0 0 1rem 0', color: '#007bff' }}>
                        🎉 Your Debt Snowball Plan is Active!
                    </h2>
                    <p style={{ margin: '0 0 2rem 0', color: '#0056b3', fontSize: '1.1rem' }}>
                        Your snowball transactions have been created successfully. Your debt payoff plan is now automated!
                    </p>
                    
                    <div className="button-row" style={{ gap: '1rem', justifyContent: 'center' }}>
                        <Link
                            className="button button-blue button-lg"
                            to="/transactions?snowball=true"
                            style={{ textDecoration: 'none' }}
                        >
                            📋 View Transactions
                        </Link>
                        <Link
                            className="button button-outline-blue button-lg"
                            to="/tools/snowball-schedule"
                            style={{ textDecoration: 'none' }}
                        >
                            📊 View Schedule
                        </Link>
                        <button
                            className="button button-outline button-lg"
                            onClick={() => setPlanCompleted(false)}
                            style={{ minWidth: '160px' }}
                        >
                            ✏️ Modify Plan
                        </button>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            {!planCompleted && (
                <ActionButtons
                    onFetchAccounts={getAccounts}
                    onRefetchAccounts={handleRefetchAccounts}
                    onCreateTransactions={handleCreateTransactions}
                    isCreating={isCreating}
                />
            )}

            {/* Main Content - Only show if plan is not completed and we have a payment account */}
            {!planCompleted && debitAccountID && hasAccounts && (
                <>
                    {/* Account List with Snowball Order */}
                    <SnowballAccountList
                        accounts={accounts}
                        onAccountToggle={handleAccountToggle}
                    />
                    
                    {/* Individual Account Details */}
                    {accounts && accounts.length > 0 && (
                        <div>
                            <h2 style={{ margin: '2rem 0 1rem 0', textAlign: 'center' }}>
                                Account Payment Plans
                            </h2>
                            {accounts.map((account) => (
                                <SnowballAccount
                                    key={account.id}
                                    account={account}
                                    onAccountUpdate={handleAccountUpdate}
                                    showSchedule={true}
                                    showSummary={false} // Progress component shows summary
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Plan Overview - Always show if we have results */}
            {(planCompleted || (!planCompleted && debitAccountID && hasAccounts)) && allAccountsHaveSchedules && aggregateResults && (
                <SnowballProgress
                    aggregateResults={aggregateResults}
                    accounts={accounts}
                    showDetailedBreakdown={true}
                    showPaymentSchedules={false}
                />
            )}

            {/* Loading State */}
            {!planCompleted && isLoadingAccounts && (
                <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    margin: '2rem 0'
                }}>
                    <p style={{ margin: '0', fontSize: '1.1rem' }}>
                        ⏳ Loading your accounts...
                    </p>
                </div>
            )}

            {/* No Accounts Message */}
            {!planCompleted && !isLoadingAccounts && !hasAccounts && (
                <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    margin: '2rem 0'
                }}>
                    <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>
                        Ready to Start Your Debt Snowball?
                    </h3>
                    <p style={{ margin: '0 0 2rem 0', color: '#666' }}>
                        Click "Fetch Accounts" above to load your debt accounts and begin creating your snowball plan.
                    </p>
                </div>
            )}

            {/* Transaction Creation Loading Overlay */}
            {isCreating && (
                <>
                    <style>
                        {`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}
                    </style>
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: '#fff',
                            padding: '3rem',
                            borderRadius: '12px',
                            textAlign: 'center',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                            maxWidth: '400px',
                            width: '90%'
                        }}>
                            <div style={{
                                width: '50px',
                                height: '50px',
                                border: '4px solid #f3f3f3',
                                borderTop: '4px solid #007bff',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                margin: '0 auto 1.5rem'
                            }}></div>
                            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>
                                Creating Transactions
                            </h3>
                            <p style={{ margin: '0', color: '#666' }}>
                                Please wait while we create your debt snowball transactions...
                            </p>
                        </div>
                    </div>
                </>
            )}

            {/* Existing Transactions Overlay - Only show when planning */}
            {!planCompleted && hasExistingTransactions && (
                <ExistingTransactionsOverlay
                    onClose={() => null}
                    onDelete={deleteExistingTransactions}
                />
            )}
        </div>
    );
};

const Snowball = () => {
    return (
        <SnowballProvider>
            <SnowballContent />
        </SnowballProvider>
    );
};

export default Snowball;